/**
 * InclusiveChoices logic:
 *
 * New requirement:
 * If a product P has InclusiveChoices (b,c,d) and any of those choices
 * is placed above P (in any of P's columns), then P must be locked
 * (cannot be dragged) until none of those allowed choices remain above.
 *
 * Existing (retained) rule:
 * While scanning upward in each column from a base with InclusiveChoices:
 *   Collect contiguous allowed products.
 *   On first disallowed encountered:
 *     If the contiguous chain length === 1 => lock that single allowed product
 *     (isolated dependent just above base).
 */
export function computeInclusiveLocks(spanModel, metadata) {
  const { productSpanInfo } = spanModel;

  // Build per-column arrays
  const columnMap = { FL: [], FR: [], RL: [], RR: [] };
  Object.values(productSpanInfo).forEach((info) => {
    info.columns.forEach((col) => {
      columnMap[col].push(info);
    });
  });
  // Sort columns by vertical bottom ascending
  Object.keys(columnMap).forEach((col) => {
    columnMap[col].sort((infoA, infoB) => infoA.bottom - infoB.bottom);
  });

  const inclusiveLocked = new Set();

  Object.values(productSpanInfo).forEach((info) => {
    const meta = metadata?.[info.product.choiceId]?.metadata;
    const inclusiveStr = meta?.InclusiveChoices;
    if (!inclusiveStr) return;

    const allowedSet = new Set(
      inclusiveStr.split(',')
        .map((choice) => choice.trim())
        .filter(Boolean)
    );

    // Normalize choiceId to string for comparison (metadata choiceIds may be numeric)
    const hasAllowedAbove = info.columns.some((col) => {
      const colArr = columnMap[col];
      const idx = colArr.findIndex((ci) => ci.product.stackItemId === info.product.stackItemId);
      if (idx === -1) return false;
      for (let i = idx + 1; i < colArr.length; i += 1) {
        const aboveInfo = colArr[i];
        if (allowedSet.has(String(aboveInfo.product.choiceId))) {
          return true;
        }
      }
      return false;
    });

    // New requirement: lock base product if any allowed appears above
    if (hasAllowedAbove) {
      inclusiveLocked.add(info.product.stackItemId);
      // Still continue to evaluate potential isolated chain locks for allowed items above
      // (not strictly required, but keeps previous behavior consistent)
    }

    // Retained isolated-chain rule per column
    info.columns.forEach((col) => {
      const colArr = columnMap[col];
      const idx = colArr.findIndex((ci) => ci.product.stackItemId === info.product.stackItemId);
      if (idx === -1) return;

      const chain = [];
      let cursor = idx + 1;
      while (cursor < colArr.length) {
        const aboveInfo = colArr[cursor];
        const isAllowed = allowedSet.has(String(aboveInfo.product.choiceId));
        if (isAllowed) {
          chain.push(aboveInfo);
          cursor += 1;
        } else {
          // First disallowed encountered
          if (chain.length === 1) {
            inclusiveLocked.add(chain[0].product.stackItemId);
          }
          break;
        }
      }
      // If column ends with allowed chain and no disallowed encountered, no isolated lock.
    });
  });

  return inclusiveLocked;
}
